import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import StarPicker from "@/components/common/StarPicker";

const EMPTY = { name: "", location: "", category: "stocks", rating: 5, quote: "", status: "published" };

const Pick = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="field" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export default function TestimonialFormDialog({ open, onOpenChange, item, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(item ? { ...EMPTY, ...item } : EMPTY); }, [open, item]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name: form.name, location: form.location, category: form.category, rating: form.rating, quote: form.quote, status: form.status };
    try {
      if (item) await api.put(`/admin/testimonials/${item.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success(`Testimonial ${item ? "updated" : "added"}`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-line bg-panel sm:max-w-lg" data-testid="testimonial-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{item ? "Edit" : "Add"} testimonial</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="tf-name">Investor name</Label>
              <Input id="tf-name" required value={form.name} onChange={set("name")} className="field" data-testid="testimonial-form-name-input" /></div>
            <div className="space-y-2"><Label htmlFor="tf-location">City (optional)</Label>
              <Input id="tf-location" value={form.location} onChange={set("location")} className="field" data-testid="testimonial-form-location-input" /></div>
            <Pick label="Investment type" value={form.category} onChange={set("category")} options={[["stocks", "Stocks"], ["real_estate", "Real estate"]]} testId="testimonial-form-category-select" />
            <Pick label="Visibility" value={form.status} onChange={set("status")} options={[["published", "Published"], ["pending", "Pending review"], ["hidden", "Hidden"]]} testId="testimonial-form-status-select" />
          </div>
          <div className="space-y-2"><Label>Rating</Label><StarPicker value={form.rating} onChange={set("rating")} testIdPrefix="testimonial-form" /></div>
          <div className="space-y-2"><Label htmlFor="tf-quote">Quote</Label>
            <Textarea id="tf-quote" required minLength={10} maxLength={500} rows={4} value={form.quote} onChange={set("quote")} className="rounded-xl border-line bg-surface" data-testid="testimonial-form-quote-input" /></div>
          <p className="text-xs text-slate-500">Only publish genuine testimonials from real investors, with their permission.</p>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="testimonial-form-submit-button">{busy ? "Saving..." : "Save testimonial"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
